/**
 * Rundanzeige für einen Anteil — Autarkiegrad, Eigenverbrauchsquote, Auslastung.
 *
 * Übernommen aus `components/data/Gauge.jsx`.
 *
 * **Sie zeigt einen Anteil, keine Menge.** Ein Ring hat ein Ende; eine Energie
 * hat keins. Wer hier Kilowattstunden hineingibt, muss ein Maximum erfinden —
 * und das ist dann eine Behauptung, keine Messung.
 *
 * Die Vorgabefarbe ist bewusst neutral und nicht die Marke: Der Ring ist eine
 * Datendarstellung (ADR 0013). Wer eine Größe zeigt, übergibt deren Farbe aus
 * `colorForQuantity(...)`.
 *
 * Zwei Kennzahlen dieses Produkts werden ständig verwechselt — Autarkiegrad und
 * Eigenverbrauchsquote, gleicher Zähler, verschiedene Nenner. Deshalb ist
 * `label` keine Zierde: Ein Ring mit „68 %" und ohne Beschriftung ist an dieser
 * Stelle nicht bloß unvollständig, sondern irreführend.
 */
import { fontWeight, quantityColorVar } from "../tokens.js";

export interface GaugeProps {
  /** Anteil in Prozent, 0…100. */
  readonly value: number;
  /** Was der Ring zeigt. Pflicht — siehe oben. */
  readonly label: string;
  readonly size?: number;
  readonly color?: string;
}

export function Gauge({ value, label, size = 150, color = quantityColorVar("consumption") }: GaugeProps) {
  const percent = Math.min(100, Math.max(0, value));
  const stroke = Math.max(8, Math.round(size * 0.075));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  /**
   * Der Ring ist an drei Vierteln offen (270°), damit unten Platz für die
   * Beschriftung bleibt — ein geschlossener Ring drängt sie in die Mitte, wo
   * schon die Zahl steht.
   */
  const arc = circumference * 0.75;

  return (
    <div
      style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 4 }}
      role="img"
      aria-label={`${label}: ${percent} Prozent`}
    >
      <div style={{ position: "relative", width: size, height: size }}>
        <svg
          width={size}
          height={size}
          aria-hidden="true"
          /* 135° gedreht: Der offene Viertelkreis liegt damit unten mittig. */
          style={{ transform: "rotate(135deg)" }}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--surface-sunken)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${arc} ${circumference}`}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${(arc * percent) / 100} ${circumference}`}
            style={{ transition: "stroke-dasharray .6s cubic-bezier(.4,0,.2,1)" }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontSize: Math.round(size * 0.23),
              fontWeight: fontWeight.semibold,
              letterSpacing: "var(--tracking-tight)",
              color: "var(--text-strong)",
            }}
          >
            {percent}
            <span style={{ fontSize: "0.5em", color: "var(--text-muted)" }}> %</span>
          </span>
        </div>
      </div>
      <span style={{ fontSize: 12.5, color: "var(--text-muted)", textAlign: "center" }}>
        {label}
      </span>
    </div>
  );
}
